import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { OnChangeValue } from "react-select";
import { useGetCustomerDefaultPricesQuery } from "store/apis/invoices";
import { useGetProductAndServiceByIdQuery } from "store/apis/productAndServices";
import { PriceInput } from "components/PriceInput/PriceInput";
import { EditProductModalContent } from "components/MyProducts/ProductFormModal";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { currency as formatCurrencyAmount } from "utils/Currency";

type PriceDropdownProps = {
  productId: string | null;
  groupId: string;
  entityId: string;
  customerId?: string;
  currency?: string;
  name: string;
  size?: "small" | "regular";
};

type PriceOption = {
  uuid: string;
  price: number;
  isDefault: boolean;
};

const parsePriceInput = (inputValue: string): number =>
  Number(inputValue.replace(/[^0-9.]/g, ""));

export const PriceDropdown = ({
  productId,
  groupId,
  entityId,
  customerId,
  currency: currencyCode,
  name,
  size = "small",
}: PriceDropdownProps) => {
  const { values, setFieldValue } = useFormikContext<{
    rate: number;
    price_id?: string | null;
  }>();
  const editProductModal = useModal();

  const { data: productDetail, isLoading: isLoadingProduct } =
    useGetProductAndServiceByIdQuery(
      { productId: productId!, entityId, groupId },
      {
        skip: !productId || !entityId || !groupId,
      }
    );

  const { data: customerDefaultPrices = [] } = useGetCustomerDefaultPricesQuery(
    { entityId, customerId: customerId! },
    { skip: !entityId || !customerId }
  );

  const linkedPrices = productDetail?.linked_price_list || [];

  const customerDefaultPrice = customerDefaultPrices.find(
    (cdp) => cdp.product_id === productId
  );

  const priceOptions: PriceOption[] = linkedPrices.map((price) => ({
    uuid: price.uuid,
    price: price.price,
    isDefault: customerDefaultPrice
      ? customerDefaultPrice.product_price.uuid === price.uuid
      : price.is_default,
  }));

  const comboboxOptions: OptionData[] = priceOptions.map((option) => ({
    value: option.uuid,
    label: (
      <div className="t-flex t-items-center t-justify-between t-w-full">
        <AmountSuperScript amount={option.price} />
        {option.isDefault && (
          <Tag tagType="blue" icon={false} size="small" rounded>
            Default
          </Tag>
        )}
      </div>
    ),
  }));

  const isStripeProduct = productDetail?.source === REVENUE_DATA_SOURCE.STRIPE;

  const matchedPriceOption = priceOptions.find(
    (option) => option.price === values.rate
  );
  const isCustomPrice =
    !values.price_id && !matchedPriceOption && Boolean(values.rate);

  const handlePriceSelect = (value: OnChangeValue<OptionData, boolean>) => {
    if (value instanceof Array) return;

    const selected = priceOptions.find((opt) => opt.uuid === value?.value);
    if (!selected) return;

    setFieldValue("rate", selected.price);
    setFieldValue("price_id", selected.uuid);
  };

  const handleCreatePrice = (inputValue: string) => {
    const parsedRate = parsePriceInput(inputValue);
    if (!parsedRate) return;

    setFieldValue("rate", parsedRate);
    setFieldValue("price_id", null);
  };

  const isValidNewPriceOption = (inputValue: string) => {
    const parsedRate = parsePriceInput(inputValue);
    return (
      parsedRate > 0 && !priceOptions.some((opt) => opt.price === parsedRate)
    );
  };

  const formatCreatePriceLabel = (inputValue: string) =>
    `Add "${formatCurrencyAmount({
      amount: parsePriceInput(inputValue),
      currencyCode,
    })}" as custom price`;

  const showCustomPriceInput =
    !productId ||
    (!isStripeProduct && (priceOptions.length === 0 || isCustomPrice));

  console.log(isStripeProduct, productId);

  if (showCustomPriceInput) {
    return (
      <PriceInput
        name={name}
        customSize={size}
        label="Unit Price"
        required
        currencyCode={currencyCode}
        showCurrencyFlag
      />
    );
  }

  const selectedOption = comboboxOptions.find(
    (option) => option.value === (values.price_id || matchedPriceOption?.uuid)
  );

  return (
    <div className="t-flex t-flex-col">
      <Combobox
        {...(!isStripeProduct ? { creatable: true } : {})}
        label="Unit Price"
        required
        size={size}
        isClearable={false}
        isLoading={isLoadingProduct}
        isDisabled={isLoadingProduct}
        options={comboboxOptions}
        value={selectedOption}
        onChange={handlePriceSelect}
        onCreateOption={handleCreatePrice}
        isValidNewOption={isValidNewPriceOption}
        formatCreateLabel={formatCreatePriceLabel}
        placeholder="Select price"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        actions={
          <ConditionalToolTip
            condition={
              isStripeProduct ? (
                <div className="t-w-64 t-text-left">
                  Price can't be added to Stripe product. <br /> Add a custom
                  product to set a new price.
                </div>
              ) : (
                false
              )
            }
            side="bottom"
            align="center"
            sideOffset={4}
          >
            <span>
              <Button
                size="small"
                customType="link"
                type="button"
                disabled={isStripeProduct}
                onClick={editProductModal.open}
              >
                Add price
              </Button>
            </span>
          </ConditionalToolTip>
        }
      />

      <Modal.Root
        open={editProductModal.isOpen}
        onOpenChange={editProductModal.close}
      >
        <EditProductModalContent
          close={editProductModal.close}
          itemId={productId}
        />
      </Modal.Root>
    </div>
  );
};
